import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import Pricing from "@/components/Pricing";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, ArrowRight, FileText, Scale, GraduationCap, Headphones, Upload, Sparkles, MessagesSquare, Shield, Server, Lock } from "lucide-react";

const USE_CASES = [
  { icon: GraduationCap, title: "textbooks.pdf", desc: "Converse with textbooks or novels. Memorize less, understand more." },
  { icon: FileText, title: "research_papers.pdf", desc: "Interrogate complex studies. Fast answers, inline citations." },
  { icon: Scale, title: "legal_agreement.pdf", desc: "Contracts, NDAs, policies — searchable in plain English." },
  { icon: Headphones, title: "customer_support.pdf", desc: "Load manuals and FAQs. Instant answers for your team." },
];

const TESTIMONIALS = [
  { name: "Jeremy", role: "Agency Owner", quote: "DocuMind is a real asset for my agency. I pull answers from books and blog posts in seconds — my workflow is unrecognisable." },
  { name: "Gemma", role: "Research Student", quote: "Fantastic for summarizing papers. Uploading multiple documents at once is a huge help for my science projects." },
  { name: "Ann", role: "Clinical Consultant", quote: "Revolutionised how I read genetic reports and consult with clients. Quick, precise, scales my practice." },
  { name: "Sean", role: "Academic Researcher", quote: "The best research tool I have found. Excellent for synthesizing across multiple sources." },
  { name: "Daniel", role: "Startup Founder", quote: "I work with teachers using DocuMind. Accurate, useful, and it fits inside real workflows." },
  { name: "Kaitlyn", role: "Legal Researcher", quote: "Crucial for simplifying legal texts and academic journals. My work has become far more accessible." },
];


const ROTATING = ["Research Papers", "Legal Contracts", "Textbooks", "Policy Docs", "Clinical Reports"];

export default function Landing() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (window.location.hash) setTimeout(() => document.querySelector(window.location.hash)?.scrollIntoView(), 100);
    const t = setInterval(() => setIdx((i) => (i + 1) % ROTATING.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {/* HERO */}
      <section className="paper-noise border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 fade-up">
            <div className="font-mono-label text-muted-foreground mb-6">Vol. I · Issue 01 · The Document Edition</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              Chat with your
              <br />
              <span className="rotator italic text-primary">{ROTATING[idx]}</span>
            </h1>
            <p className="mt-7 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Upload a PDF. Ask anything. Get grounded, citation-backed answers from a model that actually reads your document — not a vibe-check from the internet.
            </p>
            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <Link to="/signup" data-testid="hero-cta-start">
                <Button size="lg" className="bg-foreground text-background hover:bg-primary rounded-sm btn-invert px-6 h-12">
                  Start free — no card <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/compare" data-testid="hero-cta-compare" className="text-sm font-mono-label hover:text-primary">
                See how we compare →
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6">
              <div className="flex -space-x-2">
                {[0,1,2,3].map(i => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-background bg-muted overflow-hidden">
                    <img src={`https://i.pravatar.cc/64?img=${i+12}`} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              <div className="text-sm">
                <span className="font-semibold">63,577+ users</span>
                <span className="text-muted-foreground"> reading smarter today</span>
              </div>
            </div>
          </div>

          {/* Floating preview card */}
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "150ms" }}>
            <div className="relative">
              <div className="absolute -top-6 -left-4 bg-accent border border-foreground/10 px-4 py-2 font-mono-label rotate-[-3deg]">research_paper.pdf</div>
              <div className="bg-background border border-foreground/15 shadow-[8px_8px_0_0_hsl(var(--primary))] p-6">
                <div className="font-mono-label text-muted-foreground mb-3">Chat · Session 01</div>
                <div className="chat-block user mb-3 text-sm">What is the paper's main contribution and limitations?</div>
                <div className="chat-block text-sm leading-relaxed">
                  The paper introduces a <strong>sparse attention mechanism</strong> that reduces compute by 42%
                  <sup className="text-primary"> [p.4]</sup>. Limitations include memory overhead on short sequences
                  <sup className="text-primary"> [p.11]</sup>.
                </div>
                <div className="mt-3 flex gap-2">
                  <span className="font-mono-label border border-border px-2 py-1">cited: p.4</span>
                  <span className="font-mono-label border border-border px-2 py-1">cited: p.11</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-b border-border py-6 overflow-hidden bg-muted no-scrollbar">
        <div className="marquee-track">
          {[..."Harvard · Stanford · MIT · Oxford · UC Berkeley · Yale · Princeton · ETH Zurich · ".repeat(2)].join("").split("·").map((n, i) => (
            <span key={i} className="font-serif-display italic text-2xl text-muted-foreground mx-10 whitespace-nowrap">{n.trim()}</span>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="font-mono-label text-muted-foreground mb-3">How it works</div>
        <h2 className="font-serif-display text-4xl lg:text-5xl tracking-tighter max-w-2xl mb-16">
          A simpler document workflow, in three deliberate steps.
        </h2>
        <div className="grid md:grid-cols-3 border-t border-l border-border">
          {[
            { icon: Upload, step: "01", title: "Upload hundreds of PDFs in bulk", desc: "Drag in a folder. We extract text, index pages, and get out of your way." },
            { icon: Sparkles, step: "02", title: "AI reads and prepares your docs", desc: "A frontier model indexes your corpus — accurately and quickly." },
            { icon: MessagesSquare, step: "03", title: "Ask questions, chat across docs", desc: "Multi-document chat. Every answer carries a citation back to the source." },
          ].map((s) => (
            <div key={s.step} className="grid-cell p-8 lg:p-10">
              <div className="font-mono-label text-primary mb-6">Step {s.step}</div>
              <s.icon strokeWidth={1.3} className="w-10 h-10 mb-6 text-foreground" />
              <h3 className="font-serif-display text-2xl leading-tight mb-3">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES / USE CASES */}
      <section id="features" className="bg-muted border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="font-mono-label text-muted-foreground mb-3">Chat Tools</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl tracking-tighter max-w-2xl mb-16">
            Built for any kind of PDF you throw at it.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-l border-border bg-background">
            {USE_CASES.map((u) => (
              <div key={u.title} className="grid-cell p-8 min-h-[220px]">
                <u.icon strokeWidth={1.2} className="w-9 h-9 mb-4 text-primary" />
                <div className="font-mono-label mb-2 text-muted-foreground">{u.title}</div>
                <p className="text-sm leading-relaxed">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THREE BIG FEATURE BLOCKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 space-y-24">
        {[
          { kicker: "Ask Questions", title: "Conversations with PDFs, in plain language.", bullets: ["Engage with several PDFs simultaneously", "Chat across various languages", "Accurate, citation-backed answers"] },
          { kicker: "Generate Content", title: "Turn source material into first drafts.", bullets: ["Generate reports and essays", "Brainstorm ideas effortlessly", "Pull quotes directly from source"] },
          { kicker: "Train a Chatbot", title: "Share a trained chatbot anywhere.", bullets: ["Share privately with a link", "Embed directly on your website", "Visitors don't need to sign up"] },
        ].map((f, i) => (
          <div key={f.kicker} className={`grid lg:grid-cols-12 gap-10 items-center ${i % 2 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
            <div className="lg:col-span-6">
              <div className="font-mono-label text-primary mb-3">{f.kicker}</div>
              <h3 className="font-serif-display text-4xl lg:text-5xl tracking-tighter mb-6">{f.title}</h3>
              <ul className="space-y-3 mb-8">
                {f.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-base">
                    <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup">
                <Button variant="outline" className="rounded-sm border-foreground">
                  Get Started <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-6">
              <div className="aspect-[4/3] bg-muted border border-border p-6 flex items-center justify-center">
                <div className="w-full max-w-md space-y-3">
                  <div className="h-3 bg-foreground/10 rounded w-1/3"></div>
                  <div className="h-3 bg-foreground/10 rounded w-5/6"></div>
                  <div className="h-3 bg-foreground/10 rounded w-4/6"></div>
                  <div className="h-24 bg-background border border-border rounded-sm mt-6 flex items-center px-4 text-sm text-muted-foreground">
                    {f.kicker === "Ask Questions" && "“Summarize chapter 3 in 100 words with citations.”"}
                    {f.kicker === "Generate Content" && "“Write a 500-word essay from the uploaded paper.”"}
                    {f.kicker === "Train a Chatbot" && "https://documind.app/chat/your-bot"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* STATS */}
      <section className="border-y border-border bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-2 md:grid-cols-4 divide-x divide-background/10">
          {[
            ["100,000+", "Professionals"],
            ["500,000+", "Hours saved"],
            ["1M+", "Questions asked"],
            ["500%+", "Return on investment"],
          ].map(([n, l]) => (
            <div key={l} className="px-6 first:pl-0">
              <div className="font-serif-display text-5xl lg:text-6xl tracking-tighter">{n}</div>
              <div className="font-mono-label text-background/60 mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SECURITY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="font-mono-label text-muted-foreground mb-3">Security</div>
        <h2 className="font-serif-display text-4xl lg:text-5xl tracking-tighter max-w-2xl mb-16">We respect your data.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
          {[
            { icon: Server, title: "Hosted in the US", desc: "Adhering to robust data-protection standards and privacy regulations." },
            { icon: Shield, title: "Zero training", desc: "No document is ever used to train a model or shared with third parties." },
            { icon: Lock, title: "SSL / TLS end to end", desc: "All data in transit is encrypted with industry-standard protocols." },
            { icon: Check, title: "Regular audits", desc: "Comprehensive security audits keep system integrity in check." },
          ].map((b) => (
            <div key={b.title} className="grid-cell p-8">
              <b.icon strokeWidth={1.3} className="w-8 h-8 mb-4 text-primary" />
              <div className="font-serif-display text-xl mb-2">{b.title}</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <Pricing />

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="font-mono-label text-muted-foreground mb-3">Testimonials</div>
        <h2 className="font-serif-display text-4xl lg:text-5xl tracking-tighter max-w-2xl mb-16">Don't just take our word for it.</h2>
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
          {TESTIMONIALS.map((t, i) => (
            <figure key={i} className="break-inside-avoid mb-6 border border-border p-6 bg-background">
              <blockquote className="font-serif-display text-xl leading-snug italic mb-5">“{t.quote}”</blockquote>
              <figcaption className="flex items-center gap-3">
                <img src={`https://i.pravatar.cc/80?img=${i+20}`} alt="" className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="font-mono-label text-muted-foreground">{t.role}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-border bg-muted">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
          <div className="font-mono-label text-muted-foreground mb-3">FAQ</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl tracking-tighter mb-12">You asked. We answer.</h2>
          <Accordion type="single" collapsible className="w-full">
            {[
              { q: "How can DocuMind benefit my work?", a: "Extract information, summarize long documents, draft reports, and train private chatbots — all in one workspace. Teams typically save hours per week." },
              { q: "Is DocuMind helpful for students?", a: "Absolutely. Students use DocuMind to process textbooks, research papers, and study notes, with citation-backed summaries that respect academic integrity." },
              { q: "Does DocuMind support multiple languages?", a: "Yes — our models work across 100+ languages. You can upload documents in one language and chat in another." },
              { q: "How is my data protected?", a: "Documents are stored in encrypted cloud storage, served only to authenticated users, and never used to train models. See our Security section above." },
              { q: "Can I share insights with my team?", a: "Yes. On Professional and above you can share chatbots with a link or embed them directly on your website." },
              { q: "What is the refund policy?", a: "A 7-day money-back guarantee. If DocuMind isn't right for you in the first week, we refund every cent." },
            ].map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-border">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-serif-display text-xl text-left hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-28 text-center">
          <h2 className="font-serif-display text-5xl lg:text-7xl tracking-tighter mb-8">
            Unlock the potential inside<br />every document you own.
          </h2>
          <Link to="/signup" data-testid="footer-cta-start">
            <Button size="lg" className="bg-foreground text-background hover:bg-primary rounded-sm btn-invert px-8 h-14 text-base">
              Upload your first PDF <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
